import { useEffect, useRef } from "react"
import { useNavigate } from "react-router"
import BirddogLayout from "./BirddogLayout"
import {
  useSaleCreateStore,
  initialSaleData,
} from "../stores/useSaleCreateStore"
import useLocalStorage from "../../../hooks/useLocalStorage"

// Step components
import { BasicInfoStep } from "./SaleCreate/BasicInfoStep"
import { MapBoundaryStep } from "./SaleCreate/MapBoundaryStep"
import { TimberProductsStep } from "./SaleCreate/TimberProductsStep"
import { TermsConditionsStep } from "./SaleCreate/TermsConditionsStep"
import { ReviewStep } from "./SaleCreate/ReviewStep"

const STEP_TITLES = [
  "Basic Information & Scheduling",
  "Map & Boundaries",
  "Timber Products",
  "Terms & Conditions",
  "Review & Submit",
]

interface SaleFormProps {
  pageTitle: string
  pageDescription: string
  finalActionLabel: string
  localStorageKey?: string
}

export const SaleForm = ({
  pageTitle,
  pageDescription,
  finalActionLabel,
  localStorageKey = "sale-create-draft",
}: SaleFormProps) => {
  const navigate = useNavigate()
  const {
    saleData,
    currentStep,
    enabledSteps,
    isEditMode,
    setCurrentStep,
    isStepValid,
    reset,
    updateSaleData,
  } = useSaleCreateStore()

  // Progressive saving with localStorage (only for create mode)
  const [savedData, setSavedData] = useLocalStorage(localStorageKey, saleData)
  const hasLoadedSavedData = useRef(false)

  // Load saved data on mount only (create mode only)
  useEffect(() => {
    if (
      !hasLoadedSavedData.current &&
      !isEditMode &&
      savedData &&
      Object.keys(savedData).length > 0
    ) {
      hasLoadedSavedData.current = true
      updateSaleData(savedData)
    }
  }, [isEditMode, updateSaleData]) // eslint-disable-line react-hooks/exhaustive-deps

  // Save to localStorage whenever saleData changes (create mode only)
  useEffect(() => {
    if (!isEditMode) {
      setSavedData(saleData)
    }
  }, [saleData, setSavedData, isEditMode])

  const handleStepClick = (stepIndex: number) => {
    if (enabledSteps[stepIndex]) {
      setCurrentStep(stepIndex)
    }
  }

  const handleNext = () => {
    if (currentStep < STEP_TITLES.length - 1) {
      setCurrentStep(currentStep + 1)
    }
  }

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1)
    }
  }

  const handleCancel = () => {
    const confirmMessage =
      "Are you sure you want to cancel? All unsaved changes will be lost."

    if (confirm(confirmMessage)) {
      reset()
      if (!isEditMode) {
        setSavedData(initialSaleData)
      }
      navigate("/birddog")
    }
  }

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 0:
        return <BasicInfoStep />
      case 1:
        return <MapBoundaryStep />
      case 2:
        return <TimberProductsStep />
      case 3:
        return <TermsConditionsStep />
      case 4:
        return <ReviewStep />
      default:
        return <BasicInfoStep />
    }
  }

  const currentStepValid = isStepValid(currentStep)
  const isLastStep = currentStep === STEP_TITLES.length - 1

  return (
    <BirddogLayout>
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">{pageTitle}</h1>
          <p className="text-gray-600 mt-2">{pageDescription}</p>
        </div>

        {/* Stepper Navigation */}
        <div className="mb-8">
          <nav className="flex items-center justify-center">
            {STEP_TITLES.map((title, index) => {
              const isActive = index === currentStep
              const isEnabled = enabledSteps[index]
              const isCompleted = index < currentStep && isStepValid(index)

              return (
                <div key={index} className="flex items-center">
                  <button
                    onClick={() => handleStepClick(index)}
                    disabled={!isEnabled}
                    className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium transition-colors ${
                      isActive
                        ? "bg-blue-600 text-white"
                        : isCompleted
                          ? "bg-green-600 text-white"
                          : isEnabled
                            ? "bg-gray-200 text-gray-700 hover:bg-gray-300"
                            : "bg-gray-100 text-gray-400 cursor-not-allowed"
                    }`}
                  >
                    {isCompleted ? "✓" : index + 1}
                  </button>
                  <div className="ml-2 mr-6">
                    <p
                      className={`text-sm font-medium ${
                        isActive
                          ? "text-blue-600"
                          : isEnabled
                            ? "text-gray-900"
                            : "text-gray-400"
                      }`}
                    >
                      {title}
                    </p>
                  </div>
                  {index < STEP_TITLES.length - 1 && (
                    <div
                      className={`w-12 h-0.5 ${
                        isCompleted ? "bg-green-600" : "bg-gray-300"
                      }`}
                    />
                  )}
                </div>
              )
            })}
          </nav>
        </div>

        {/* Step Content */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="mb-6">
            <h2 className="text-2xl font-semibold text-gray-900">
              {STEP_TITLES[currentStep]}
            </h2>
          </div>

          {renderCurrentStep()}
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-between">
          <div>
            <button
              onClick={handleCancel}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
          </div>

          <div className="flex space-x-3">
            {currentStep > 0 && (
              <button
                onClick={handlePrevious}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                Previous
              </button>
            )}

            {!isLastStep ? (
              <button
                onClick={handleNext}
                disabled={!currentStepValid}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  currentStepValid
                    ? "bg-blue-600 text-white hover:bg-blue-700"
                    : "bg-gray-400 text-gray-600 cursor-not-allowed"
                }`}
              >
                Next
              </button>
            ) : (
              <button
                disabled={!currentStepValid}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  currentStepValid
                    ? "bg-green-600 text-white hover:bg-green-700"
                    : "bg-gray-400 text-gray-600 cursor-not-allowed"
                }`}
              >
                {finalActionLabel}
              </button>
            )}
          </div>
        </div>
      </div>
    </BirddogLayout>
  )
}
